import type {
  MessageReport,
  ModerationReportedMessage,
  ModerationReportStatus,
  ModerationReportType,
  ModerationResolution,
  ModerationUser,
  ReportReason,
  UserReport,
} from '@pulsify/shared'
import { CheckCircle2, ChevronLeft, ChevronRight, ShieldAlert, XCircle } from 'lucide-react'
import { useState } from 'react'
import {
  useMessageReports,
  useResolveModerationReport,
  useUserReports,
} from '~/entities/moderation/model/use-moderation-reports'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'

const REASON_LABELS: Record<ReportReason, () => string> = {
  spam: m.moderationReasonSpam,
  harassment: m.moderationReasonHarassment,
  inappropriate: m.moderationReasonInappropriate,
  other: m.moderationReasonOther,
}

const CONVERSATION_LABELS: Record<ModerationReportedMessage['conversationType'], () => string> = {
  plan: m.moderationConversationPlan,
  direct: m.moderationConversationDirect,
  group: m.moderationConversationGroup,
  channel: m.moderationConversationChannel,
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString(getLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function UserCell({ user }: { user: ModerationUser | null }) {
  if (!user) {
    return <span className="text-slate-500 dark:text-slate-400">{m.moderationDeletedUser()}</span>
  }
  return (
    <div className="min-w-0">
      <p className="text-slate-900 dark:text-slate-100 break-words">{user.displayName}</p>
      {user.username && (
        <p className="text-slate-500 dark:text-slate-400 text-xs break-words">@{user.username}</p>
      )}
    </div>
  )
}

function MessageCell({ message }: { message: ModerationReportedMessage }) {
  return (
    <div className="space-y-1 min-w-0">
      <Badge variant="outline">{message.kind}</Badge>
      {message.deleted ? (
        <p className="text-slate-500 dark:text-slate-400 italic">{m.moderationMessageDeleted()}</p>
      ) : message.body ? (
        <p className="text-slate-900 dark:text-slate-100 break-words whitespace-pre-wrap">
          {message.body}
        </p>
      ) : (
        <p className="text-slate-500 dark:text-slate-400">{m.moderationNoTextContent()}</p>
      )}
      <p className="text-slate-500 dark:text-slate-400 text-xs">{formatDate(message.createdAt)}</p>
    </div>
  )
}

function ResolutionCell({ resolution }: { resolution: ModerationResolution }) {
  return (
    <div className="space-y-1">
      <Badge variant="secondary">
        {resolution.action === 'actioned'
          ? m.moderationActionedBadge()
          : m.moderationDismissedBadge()}
      </Badge>
      <p className="text-slate-500 dark:text-slate-400 text-xs">
        {formatDate(resolution.resolvedAt)}
      </p>
      {resolution.resolvedByName && (
        <p className="text-slate-500 dark:text-slate-400 text-xs">
          {m.moderationReviewedBy({ name: resolution.resolvedByName })}
        </p>
      )}
    </div>
  )
}

function ActionsCell({
  type,
  report,
}: {
  type: ModerationReportType
  report: { id: string; resolution: ModerationResolution | null }
}) {
  const resolve = useResolveModerationReport()

  if (report.resolution) {
    return <ResolutionCell resolution={report.resolution} />
  }

  return (
    <div className="flex justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={resolve.isPending}
        onClick={() => resolve.mutate({ type, id: report.id, action: 'actioned' })}
      >
        <CheckCircle2 className="mr-1 w-4 h-4" />
        {m.moderationActionActioned()}
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={resolve.isPending}
        onClick={() => resolve.mutate({ type, id: report.id, action: 'dismissed' })}
      >
        <XCircle className="mr-1 w-4 h-4" />
        {m.moderationActionDismiss()}
      </Button>
    </div>
  )
}

function QueueState({ children }: { children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16">{children}</CardContent>
    </Card>
  )
}

function Pager({
  page,
  totalPages,
  onChange,
}: {
  page: number
  totalPages: number
  onChange: (page: number) => void
}) {
  if (totalPages <= 1) return null
  return (
    <div className="flex justify-between items-center mt-4">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        <ChevronLeft className="mr-1 w-4 h-4" />
        {m.moderationPrevious()}
      </Button>
      <span className="text-slate-600 dark:text-slate-400 text-sm">
        {m.moderationPageIndicator({ page: String(page), totalPages: String(totalPages) })}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        {m.moderationNext()}
        <ChevronRight className="ml-1 w-4 h-4" />
      </Button>
    </div>
  )
}

const HEAD_CELL = 'px-4 py-3 font-medium'
const HEAD_ROW =
  'border-slate-200 dark:border-slate-800 border-b text-left text-slate-500 dark:text-slate-400'
const BODY = 'divide-y divide-slate-200 dark:divide-slate-800'
const CELL = 'px-4 py-3 align-top'

function MessageReportsTable({
  reports,
  isLoading,
  hasError,
}: {
  reports: MessageReport[] | undefined
  isLoading: boolean
  hasError: boolean
}) {
  if (isLoading) {
    return (
      <QueueState>
        <div className="border-blue-600 border-t-4 rounded-full w-12 h-12 animate-spin" />
      </QueueState>
    )
  }
  if (hasError) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-destructive text-sm">{m.moderationLoadError()}</p>
        </CardContent>
      </Card>
    )
  }
  if (!reports || reports.length === 0) {
    return (
      <QueueState>
        <ShieldAlert className="w-12 h-12 text-slate-400" />
        <p className="text-slate-600 dark:text-slate-400 text-sm">{m.moderationEmpty()}</p>
      </QueueState>
    )
  }

  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={HEAD_ROW}>
              <th className={HEAD_CELL}>{m.moderationColumnReported()}</th>
              <th className={HEAD_CELL}>{m.moderationColumnSender()}</th>
              <th className={HEAD_CELL}>{m.moderationColumnMessage()}</th>
              <th className={HEAD_CELL}>{m.moderationColumnConversation()}</th>
              <th className={HEAD_CELL}>{m.moderationColumnReporter()}</th>
              <th className={HEAD_CELL}>{m.moderationColumnReason()}</th>
              <th className={HEAD_CELL}>{m.details()}</th>
              <th className={cn(HEAD_CELL, 'text-right')}>{m.moderationColumnActions()}</th>
            </tr>
          </thead>
          <tbody className={BODY}>
            {reports.map((report) => (
              <tr key={report.id}>
                <td className={cn(CELL, 'whitespace-nowrap text-slate-600 dark:text-slate-400')}>
                  {formatDate(report.createdAt)}
                </td>
                <td className={CELL}>
                  <UserCell user={report.message.sender} />
                </td>
                <td className={cn(CELL, 'max-w-72')}>
                  <MessageCell message={report.message} />
                </td>
                <td className={cn(CELL, 'whitespace-nowrap')}>
                  <Badge variant="secondary">
                    {CONVERSATION_LABELS[report.message.conversationType]()}
                  </Badge>
                </td>
                <td className={CELL}>
                  <UserCell user={report.reporter} />
                </td>
                <td className={cn(CELL, 'whitespace-nowrap text-slate-900 dark:text-slate-100')}>
                  {REASON_LABELS[report.reason]()}
                </td>
                <td className={cn(CELL, 'max-w-56 break-words text-slate-600 dark:text-slate-400')}>
                  {report.details ?? '-'}
                </td>
                <td className={cn(CELL, 'text-right')}>
                  <ActionsCell type="message" report={report} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

function UserReportsTable({
  reports,
  isLoading,
  hasError,
}: {
  reports: UserReport[] | undefined
  isLoading: boolean
  hasError: boolean
}) {
  if (isLoading) {
    return (
      <QueueState>
        <div className="border-blue-600 border-t-4 rounded-full w-12 h-12 animate-spin" />
      </QueueState>
    )
  }
  if (hasError) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-destructive text-sm">{m.moderationLoadError()}</p>
        </CardContent>
      </Card>
    )
  }
  if (!reports || reports.length === 0) {
    return (
      <QueueState>
        <ShieldAlert className="w-12 h-12 text-slate-400" />
        <p className="text-slate-600 dark:text-slate-400 text-sm">{m.moderationEmpty()}</p>
      </QueueState>
    )
  }

  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={HEAD_ROW}>
              <th className={HEAD_CELL}>{m.moderationColumnReported()}</th>
              <th className={HEAD_CELL}>{m.moderationColumnUser()}</th>
              <th className={HEAD_CELL}>{m.moderationColumnReporter()}</th>
              <th className={HEAD_CELL}>{m.moderationColumnReason()}</th>
              <th className={HEAD_CELL}>{m.details()}</th>
              <th className={cn(HEAD_CELL, 'text-right')}>{m.moderationColumnActions()}</th>
            </tr>
          </thead>
          <tbody className={BODY}>
            {reports.map((report) => (
              <tr key={report.id}>
                <td className={cn(CELL, 'whitespace-nowrap text-slate-600 dark:text-slate-400')}>
                  {formatDate(report.createdAt)}
                </td>
                <td className={CELL}>
                  <UserCell user={report.target} />
                  <p className="mt-1 text-slate-500 dark:text-slate-400 text-xs">
                    {m.moderationTargetReportCount({ count: String(report.targetReportCount) })}
                  </p>
                </td>
                <td className={CELL}>
                  <UserCell user={report.reporter} />
                </td>
                <td className={cn(CELL, 'whitespace-nowrap text-slate-900 dark:text-slate-100')}>
                  {REASON_LABELS[report.reason]()}
                </td>
                <td className={cn(CELL, 'max-w-64 break-words text-slate-600 dark:text-slate-400')}>
                  {report.details ?? '-'}
                </td>
                <td className={cn(CELL, 'text-right')}>
                  <ActionsCell type="user" report={report} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

function MessageQueue({ status }: { status: ModerationReportStatus }) {
  const [page, setPage] = useState(1)
  const { data, isLoading, error } = useMessageReports(status, page)
  const totalPages = data?.pagination.totalPages ?? 0

  return (
    <>
      <MessageReportsTable reports={data?.data} isLoading={isLoading} hasError={!!error} />
      <Pager page={page} totalPages={totalPages} onChange={setPage} />
    </>
  )
}

function UserQueue({ status }: { status: ModerationReportStatus }) {
  const [page, setPage] = useState(1)
  const { data, isLoading, error } = useUserReports(status, page)
  const totalPages = data?.pagination.totalPages ?? 0

  return (
    <>
      <UserReportsTable reports={data?.data} isLoading={isLoading} hasError={!!error} />
      <Pager page={page} totalPages={totalPages} onChange={setPage} />
    </>
  )
}

export function ModerationPage() {
  const [type, setType] = useState<ModerationReportType>('message')
  const [status, setStatus] = useState<ModerationReportStatus>('pending')

  return (
    <div className="p-4 py-8 sm:p-8 sm:py-24">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8">
          <h1 className="mb-2 font-bold text-slate-900 dark:text-slate-100 text-3xl">
            {m.moderationQueue()}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">{m.moderationQueueDescription()}</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {(['message', 'user'] as const).map((tab) => (
            <Button
              key={tab}
              variant={type === tab ? 'default' : 'outline'}
              size="sm"
              onClick={() => setType(tab)}
              className={cn(type === tab && 'bg-blue-600 hover:bg-blue-700 text-white')}
            >
              {tab === 'message' ? m.moderationTabMessages() : m.moderationTabUsers()}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {(['pending', 'resolved'] as const).map((tab) => (
            <Button
              key={tab}
              variant={status === tab ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatus(tab)}
              className={cn(status === tab && 'bg-blue-600 hover:bg-blue-700 text-white')}
            >
              {tab === 'pending' ? m.moderationStatusPending() : m.moderationStatusReviewed()}
            </Button>
          ))}
        </div>

        {type === 'message' ? (
          <MessageQueue key={`message-${status}`} status={status} />
        ) : (
          <UserQueue key={`user-${status}`} status={status} />
        )}
      </div>
    </div>
  )
}
